import { useCallback, useEffect, useMemo, useState, type FormEvent } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import * as api from '../services/api';
import { ConfirmNoteModal } from '../components/ConfirmNoteModal';
import { FilterBar } from '../components/FilterBar';
import { Modal } from '../components/Modal';
import { StatusBadge } from '../components/StatusBadge';
import { useAuth } from '../context/AuthContext';
import type { ManagedUser } from '../types/auth';
import type {
  School,
  SchoolLifecycleAction,
  SchoolStatus,
  UpdateSchoolInput
} from '../types/school';

type TabId = 'pending' | 'directory';

const PAGE_SIZE = 10;
const SCHOOLS_STATE_KEY = 'tfm_admin_schools_state';

const STATUS_FILTERS: Array<SchoolStatus | ''> = [
  '',
  'pending',
  'approved',
  'suspended',
  'rejected'
];

const SCHOOL_LIFECYCLE: SchoolLifecycleAction[] = ['deactivate', 'suspend', 'reject'];

interface SchoolsPageState {
  tab: TabId;
  page: number;
  status: SchoolStatus | '';
  schoolId: string | null;
}

interface NoteTarget {
  school: School;
  action: SchoolLifecycleAction;
  kind: 'reject' | 'lifecycle';
}

function isTabId(value: string | null): value is TabId {
  return value === 'pending' || value === 'directory';
}

function resolveTab(value: string | null | undefined): TabId | null {
  if (value === 'register') return 'directory';
  if (isTabId(value ?? null)) return value as TabId;
  return null;
}

function isSchoolStatus(value: string | null): value is SchoolStatus {
  return (
    value === 'pending' ||
    value === 'approved' ||
    value === 'suspended' ||
    value === 'rejected'
  );
}

function readStoredState(): Partial<SchoolsPageState> {
  try {
    const raw = sessionStorage.getItem(SCHOOLS_STATE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as Partial<SchoolsPageState> & { tab?: string };
    return {
      ...parsed,
      tab: resolveTab(parsed.tab ?? null) ?? undefined
    };
  } catch {
    return {};
  }
}

function writeStoredState(state: SchoolsPageState): void {
  sessionStorage.setItem(SCHOOLS_STATE_KEY, JSON.stringify(state));
}

function resolveInitialState(searchParams: URLSearchParams): SchoolsPageState {
  const stored = readStoredState();
  const tabParam = searchParams.get('tab');
  const pageParam = Number(searchParams.get('page'));
  const statusParam = searchParams.get('status');
  const schoolParam = searchParams.get('school');

  return {
    tab: resolveTab(tabParam) ?? resolveTab(stored.tab ?? null) ?? 'directory',
    page:
      Number.isFinite(pageParam) && pageParam >= 1
        ? Math.floor(pageParam)
        : typeof stored.page === 'number' && stored.page >= 1
          ? stored.page
          : 1,
    status: isSchoolStatus(statusParam)
      ? statusParam
      : isSchoolStatus(stored.status ?? null)
        ? stored.status!
        : '',
    schoolId: schoolParam || stored.schoolId || null
  };
}

export function SchoolsPage() {
  const { isSuperAdmin } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const initial = useMemo(() => resolveInitialState(searchParams), []);

  const [tab, setTab] = useState<TabId>(isSuperAdmin ? initial.tab : 'directory');
  const [page, setPage] = useState(initial.page);
  const [schools, setSchools] = useState<School[]>([]);
  const [statusFilter, setStatusFilter] = useState<SchoolStatus | ''>(initial.status);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [busySchoolId, setBusySchoolId] = useState<string | null>(null);

  const [selectedId, setSelectedId] = useState<string | null>(initial.schoolId);
  const [selected, setSelected] = useState<School | null>(null);
  const [adminUsers, setAdminUsers] = useState<ManagedUser[]>([]);
  const [detailLoading, setDetailLoading] = useState(false);
  const [editName, setEditName] = useState('');
  const [editShortName, setEditShortName] = useState('');
  const [editEmail, setEditEmail] = useState('');
  const [editPhone, setEditPhone] = useState('');
  const [savingDetail, setSavingDetail] = useState(false);

  const [noteTarget, setNoteTarget] = useState<NoteTarget | null>(null);

  useEffect(() => {
    if (!isSuperAdmin && tab !== 'directory') {
      setTab('directory');
    }
  }, [isSuperAdmin, tab]);

  useEffect(() => {
    const state: SchoolsPageState = {
      tab,
      page,
      status: statusFilter,
      schoolId: selectedId
    };
    writeStoredState(state);

    const next = new URLSearchParams();
    if (isSuperAdmin && tab !== 'directory') next.set('tab', tab);
    if (page > 1) next.set('page', String(page));
    if (statusFilter) next.set('status', statusFilter);
    if (selectedId) next.set('school', selectedId);
    setSearchParams(next, { replace: true });
  }, [tab, page, statusFilter, selectedId, isSuperAdmin, setSearchParams]);

  const loadSchools = useCallback(async (status?: SchoolStatus | '') => {
    setLoading(true);
    setError(null);
    try {
      const items = await api.listSchools({ status: status || undefined });
      setSchools(items);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load schools');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const status = tab === 'pending' ? 'pending' : statusFilter;
    void loadSchools(status);
  }, [tab, statusFilter, loadSchools]);

  const listSource = useMemo(() => {
    if (tab !== 'directory' || !search.trim()) return schools;
    const q = search.trim().toLowerCase();
    return schools.filter((school) => {
      const city = school.address?.city ?? '';
      return (
        school.name.toLowerCase().includes(q) ||
        school.shortName.toLowerCase().includes(q) ||
        city.toLowerCase().includes(q)
      );
    });
  }, [schools, search, tab]);

  const totalPages = Math.max(1, Math.ceil(listSource.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const pagedSchools = listSource.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);
  const rangeStart = listSource.length === 0 ? 0 : (safePage - 1) * PAGE_SIZE + 1;
  const rangeEnd = Math.min(safePage * PAGE_SIZE, listSource.length);

  useEffect(() => {
    if (page !== safePage) setPage(safePage);
  }, [page, safePage]);

  useEffect(() => {
    if (!selectedId) {
      setSelected(null);
      setAdminUsers([]);
      return;
    }

    let cancelled = false;

    async function loadDetail() {
      setDetailLoading(true);
      setError(null);
      try {
        const school = await api.getSchool(selectedId!);
        const users = await api.listUsers({
          schoolId: selectedId!,
          role: 'SCHOOL_ADMIN',
          limit: 10
        });
        if (!cancelled) {
          setSelected(school);
          setEditName(school.name);
          setEditShortName(school.shortName);
          setEditEmail(school.contactInfo?.email ?? '');
          setEditPhone(school.contactInfo?.phone ?? '');
          setAdminUsers(users.items);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Failed to load school');
        }
      } finally {
        if (!cancelled) setDetailLoading(false);
      }
    }

    void loadDetail();
    return () => {
      cancelled = true;
    };
  }, [selectedId]);

  async function handleApprove(school: School) {
    setBusySchoolId(school.id);
    setError(null);
    setSuccess(null);
    try {
      await api.approveSchoolRegistration(school.adminUserId);
      setSuccess(`Approved ${school.name}. School admin account activated.`);
      await loadSchools(tab === 'pending' ? 'pending' : statusFilter);
      if (selectedId === school.id) setSelectedId(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to approve school');
    } finally {
      setBusySchoolId(null);
    }
  }

  function openReject(school: School) {
    setNoteTarget({ school, action: 'reject', kind: 'reject' });
  }

  function openLifecycle(school: School, action: SchoolLifecycleAction) {
    setNoteTarget({ school, action, kind: 'lifecycle' });
  }

  async function confirmNoteAction(note: string) {
    if (!noteTarget) return;
    const { school, action, kind } = noteTarget;
    setBusySchoolId(school.id);
    setError(null);
    setSuccess(null);
    try {
      if (kind === 'reject') {
        await api.applySchoolLifecycleAction(school.id, 'reject', note || undefined);
        setSuccess(`Rejected ${school.name}.`);
        await loadSchools(tab === 'pending' ? 'pending' : statusFilter);
        if (selectedId === school.id) setSelectedId(null);
      } else {
        const updated = await api.applySchoolLifecycleAction(
          school.id,
          action,
          note || undefined
        );
        setSchools((current) => current.map((item) => (item.id === updated.id ? updated : item)));
        if (selected?.id === updated.id) setSelected(updated);
        setSuccess(`${action} applied to ${school.name}.`);
      }
      setNoteTarget(null);
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : kind === 'reject'
            ? 'Failed to reject school'
            : `Failed to ${action} school`
      );
    } finally {
      setBusySchoolId(null);
    }
  }

  async function handleSaveDetail(event: FormEvent) {
    event.preventDefault();
    if (!selected) return;
    setSavingDetail(true);
    setError(null);
    setSuccess(null);
    try {
      const body: UpdateSchoolInput = {
        name: editName.trim(),
        shortName: editShortName.trim(),
        contactInfo: {
          email: editEmail.trim() || undefined,
          phone: editPhone.trim() || undefined
        }
      };
      const updated = await api.updateSchool(selected.id, body);
      setSelected(updated);
      setSchools((current) => current.map((item) => (item.id === updated.id ? updated : item)));
      setSuccess('School details saved.');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update school');
    } finally {
      setSavingDetail(false);
    }
  }

  function closeEditModal() {
    setSelectedId(null);
  }

  return (
    <section className="admin-panel schools-page">
      <div className="panel-heading">
        <div>
          <h2>{isSuperAdmin ? 'Schools' : 'My school'}</h2>
          <p>
            {isSuperAdmin
              ? 'Approve pending registrations and manage existing school records.'
              : 'View and update your school profile. Platform lifecycle actions remain Super Admin only.'}
          </p>
        </div>
        <div className="action-row">
          {isSuperAdmin ? (
            <Link to="/schools/register" className="primary-btn">
              Register school
            </Link>
          ) : null}
          <span className="panel-meta">{listSource.length} total</span>
        </div>
      </div>

      {isSuperAdmin ? (
        <div className="tab-bar" role="tablist" aria-label="School management sections">
          {(
            [
              { id: 'directory', label: 'School directory' },
              { id: 'pending', label: 'Pending approvals' }
            ] as const
          ).map((item) => (
            <button
              key={item.id}
              type="button"
              role="tab"
              aria-selected={tab === item.id}
              className={tab === item.id ? 'tab-btn active' : 'tab-btn'}
              onClick={() => {
                setTab(item.id);
                setPage(1);
                setSelectedId(null);
                setError(null);
                setSuccess(null);
              }}
            >
              {item.label}
            </button>
          ))}
        </div>
      ) : null}

      {error ? (
        <p className="form-error" role="alert">
          {error}
        </p>
      ) : null}
      {success ? (
        <p className="form-success" role="status">
          {success}
        </p>
      ) : null}

      {tab === 'directory' ? (
        <FilterBar
          onSubmit={(e) => {
            e.preventDefault();
            setPage(1);
            setSearch(searchInput.trim());
          }}
        >
          <label>
            Search
            <input
              type="search"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder="Name, short name, or city"
            />
          </label>
          <label>
            Status
            <select
              value={statusFilter}
              onChange={(e) => {
                setPage(1);
                setStatusFilter(e.target.value as SchoolStatus | '');
              }}
            >
              {STATUS_FILTERS.map((option) => (
                <option key={option || 'all'} value={option}>
                  {option || 'All statuses'}
                </option>
              ))}
            </select>
          </label>
          <button type="submit" className="primary-btn">
            Apply
          </button>
          <button
            type="button"
            className="ghost-btn"
            onClick={() => void loadSchools(statusFilter)}
          >
            Refresh
          </button>
        </FilterBar>
      ) : null}

      {loading ? <p className="muted">Loading schools…</p> : null}

      {!loading && listSource.length === 0 ? (
        <p className="muted">
          {tab === 'pending' ? 'No pending school registrations.' : 'No schools match these filters.'}
        </p>
      ) : null}

      {!loading && listSource.length > 0 ? (
        <>
          <div className="table-wrap">
            <table className="data-table">
              <thead>
                <tr>
                  <th>School</th>
                  <th>Status</th>
                  <th>Active</th>
                  <th>Contact</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {pagedSchools.map((school) => (
                  <tr key={school.id}>
                    <td>
                      <strong>{school.name}</strong>
                      <div className="cell-sub">{school.shortName}</div>
                    </td>
                    <td>
                      <StatusBadge status={school.status} />
                    </td>
                    <td>{school.isActive ? 'Yes' : 'No'}</td>
                    <td>{school.contactInfo?.email || school.contactInfo?.phone || '—'}</td>
                    <td>
                      <div className="action-row">
                        <button
                          type="button"
                          className="ghost-btn compact"
                          onClick={() => setSelectedId(school.id)}
                        >
                          Edit
                        </button>
                        {isSuperAdmin ? (
                          <>
                            {school.status === 'pending' ? (
                              <>
                                <button
                                  type="button"
                                  className="ghost-btn compact"
                                  disabled={busySchoolId === school.id}
                                  onClick={() => void handleApprove(school)}
                                >
                                  Approve
                                </button>
                                <button
                                  type="button"
                                  className="ghost-btn compact"
                                  disabled={busySchoolId === school.id}
                                  onClick={() => openReject(school)}
                                >
                                  Reject
                                </button>
                              </>
                            ) : null}
                            {tab === 'directory' && school.status === 'approved'
                              ? SCHOOL_LIFECYCLE.map((action) => (
                                  <button
                                    key={action}
                                    type="button"
                                    className="ghost-btn compact"
                                    disabled={busySchoolId === school.id}
                                    onClick={() => openLifecycle(school, action)}
                                  >
                                    {action}
                                  </button>
                                ))
                              : null}
                          </>
                        ) : null}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="school-list-pager" role="navigation" aria-label="School list pagination">
            <span className="school-list-pager-range">
              {rangeStart}-{rangeEnd} of {listSource.length}
            </span>
            <button
              type="button"
              className="pager-chevron"
              aria-label="Previous page"
              disabled={safePage <= 1}
              onClick={() => setPage((current) => Math.max(1, current - 1))}
            >
              ‹
            </button>
            <button
              type="button"
              className="pager-chevron"
              aria-label="Next page"
              disabled={safePage >= totalPages}
              onClick={() => setPage((current) => Math.min(totalPages, current + 1))}
            >
              ›
            </button>
          </div>
        </>
      ) : null}

      <Modal
        open={Boolean(selectedId)}
        title={selected?.name ?? 'Edit school'}
        subtitle={
          selected
            ? `${selected.status}${selected.isActive ? ' · Active' : ' · Inactive'}`
            : undefined
        }
        onClose={closeEditModal}
        closeDisabled={savingDetail}
        asForm
        onSubmit={(event) => void handleSaveDetail(event)}
        size="lg"
        actions={
          <>
            <button
              type="button"
              className="ghost-btn"
              disabled={savingDetail}
              onClick={closeEditModal}
            >
              Close
            </button>
            <button type="submit" className="primary-btn" disabled={savingDetail || detailLoading || !selected}>
              {savingDetail ? 'Saving…' : 'Save changes'}
            </button>
          </>
        }
      >
        {detailLoading || !selected ? (
          <p className="muted">Loading school…</p>
        ) : (
          <>
            <dl className="detail-list">
              <div className="detail-list-row">
                <div>
                  <dt>School ID</dt>
                  <dd>{selected.id}</dd>
                </div>
                <div>
                  <dt>Admin user ID</dt>
                  <dd>{selected.adminUserId}</dd>
                </div>
              </div>
              <div>
                <dt>Address</dt>
                <dd>
                  {[
                    selected.address?.street,
                    selected.address?.city,
                    selected.address?.state,
                    selected.address?.postalCode,
                    selected.address?.country
                  ]
                    .filter(Boolean)
                    .join(', ') || '—'}
                </dd>
              </div>
            </dl>

            {adminUsers.length > 0 ? (
              <div className="admin-users-block">
                <h4>School admin</h4>
                {adminUsers.map((user) => (
                  <div key={user.id} className="admin-user-row">
                    <div className="admin-user-main">
                      <strong>{user.fullName}</strong>
                      <StatusBadge status={user.status} />
                    </div>
                    <span>
                      {user.email} · {user.username}
                    </span>
                  </div>
                ))}
              </div>
            ) : null}

            <div className="form-grid form-grid-tight">
              <label>
                Name
                <input required value={editName} onChange={(e) => setEditName(e.target.value)} />
              </label>
              <label>
                Short name
                <input
                  required
                  value={editShortName}
                  onChange={(e) => setEditShortName(e.target.value)}
                />
              </label>
              <label>
                Contact email
                <input
                  type="email"
                  value={editEmail}
                  onChange={(e) => setEditEmail(e.target.value)}
                />
              </label>
              <label>
                Contact phone
                <input value={editPhone} onChange={(e) => setEditPhone(e.target.value)} />
              </label>
            </div>
          </>
        )}
      </Modal>

      <ConfirmNoteModal
        open={Boolean(noteTarget)}
        title={
          noteTarget
            ? noteTarget.kind === 'reject'
              ? 'Reject school'
              : `${noteTarget.action} school`
            : 'Confirm'
        }
        message={
          noteTarget
            ? noteTarget.kind === 'reject'
              ? `Reject ${noteTarget.school.name}?`
              : `Apply “${noteTarget.action}” to ${noteTarget.school.name}?`
            : ''
        }
        confirmLabel={
          noteTarget?.kind === 'reject' ? 'Reject' : (noteTarget?.action ?? 'Confirm')
        }
        noteLabel="Optional reason"
        busy={Boolean(noteTarget && busySchoolId === noteTarget.school.id)}
        onCancel={() => setNoteTarget(null)}
        onConfirm={confirmNoteAction}
      />
    </section>
  );
}
